import { toRelatedSourceKeys } from '@/hooks';
import { useDataSourceStore } from '@/store/use-data-source-store';
import { Solar } from 'lunar-javascript';
import { sexagenaryCycleArr } from './data-source';
import { cloneDeep } from 'lodash-es';
import { DataSourceKey } from './enum';

interface RecordSourceItem {
  sourcekey: string;
  values: (string | number)[];
  interval: number;
  unAssociate?: boolean;
}

// 旧的动效预览规则，期望默认值->最大值
const getValues = (min: number, max: number, step = 1) => {
  const values: number[] = [];
  let cur = min;
  while (cur < max) {
    values.push(cur);
    cur += step;
  }
  if (values[values.length - 1] !== max) values.push(max);
  return values;
};

// 新的动效预览规则，期望默认值->最大值->最小值->默认值
const newGetValues = (
  minValue: number,
  maxValue: number,
  defaultValue: number,
  step: number
) => {
  const result: number[] = [];

  for (let i = defaultValue; i < maxValue; i += step) {
    result.push(i);
  }
  result.push(maxValue);

  for (let i = minValue; i < defaultValue; i += step) {
    result.push(i);
  }
  result.push(defaultValue);

  return result;
};

// const getTimeRange = () => {
//   const hours = getValues(9, 23);
//   const minutes = getValues(28, 59);
//   const seconds = getValues(0, 59);

//   const total = 35 * seconds.length;
//   return {
//     hour: {
//       values: hours,
//       interval: total / hours.length
//     },
//     minute: {
//       values: minutes,
//       interval: (total / minutes.length) * 1.3
//     },
//     second: {
//       values: seconds,
//       interval: 33
//     }
//   };
// };

export const getRecordDataSource = (recordSourceKeys: string[]) => {
  const dataSourceStore = useDataSourceStore();
  const dataSourceDefault = dataSourceStore.wholeValues(undefined);
  const dateYearStart = Number(dataSourceDefault.dateYear);

  // const dateWeek = Number(dataSourceStore.wholeValues(undefined).dateWeek);

  const lunar = Solar.fromYmd(dateYearStart, 6, 1).getLunar();
  const ganzhi = lunar.getYearInGanZhi();
  const ganzhiIndex = sexagenaryCycleArr.findIndex((item) => item == ganzhi);

  // 获取日期

  // const timeRange = getTimeRange();
  // console.log('总时长=' + getValues(0, 59, 0.5).length * 16 + 'ms');

  /**
   * interval 时间间隔设置规则
   * 1-5项 150ms
   * 6-10项 120ms
   * 11-15项 90ms
   * 16+项 60ms
   * ...
   */
  const wholeItems: RecordSourceItem[] = [
    {
      sourcekey: 'timeSecond',
      interval: 16,
      values: newGetValues(0, 59.99, Number(dataSourceDefault.timeSecond), 10)
    },
    {
      sourcekey: 'timeMinute',
      interval: 13,
      values: newGetValues(0, 59.99, Number(dataSourceDefault.timeMinute), 10)
    },
    {
      sourcekey: 'timeHour',
      interval: 50,
      values: newGetValues(0, 23.99, Number(dataSourceDefault.timeHour), 0.1)
    },
    //
    {
      sourcekey: 'dateYear',
      interval: 180,
      values: getValues(dateYearStart, 2045, 1)
    },
    {
      sourcekey: 'dateMonth',
      interval: 180,
      values: newGetValues(1, 12, Number(dataSourceDefault.dateMonth), 1)
    },
    {
      sourcekey: 'dateDay',
      interval: 180,
      values: newGetValues(1, 31, Number(dataSourceDefault.dateDay), 1) //日应与年和月关联
    },
    {
      sourcekey: 'dualtimeHour',
      interval: 12,
      values: newGetValues(
        0,
        23.99,
        Number(dataSourceDefault.dualtimeHour),
        0.1
      )
    },
    {
      sourcekey: 'dualtimeMinute',
      interval: 13,
      values: newGetValues(
        0,
        59.99,
        Number(dataSourceDefault.dualtimeMinute),
        10
      )
    },
    {
      sourcekey: 'dualtimeSecond',
      interval: 16,
      values: newGetValues(
        0,
        59.99,
        Number(dataSourceDefault.dualtimeSecond),
        10
      )
    },
    {
      sourcekey: 'healthStepCount',
      interval: 1000,
      values: newGetValues(
        0,
        2580,
        Number(dataSourceDefault.healthStepCount),
        100
      )
    },
    {
      sourcekey: 'healthStepProgress',
      interval: 150,
      values: newGetValues(
        0,
        100,
        Number(dataSourceDefault.healthStepProgress),
        10
      )
    },
    {
      sourcekey: 'healthStepTarget',
      interval: 100,
      values: newGetValues(
        0,
        3220,
        Number(dataSourceDefault.healthStepTarget),
        100
      )
    },
    {
      sourcekey: 'healthStepKiloMeter',
      interval: 150,
      values: newGetValues(
        0,
        100,
        Number(dataSourceDefault.healthStepKiloMeter),
        10
      )
    },
    {
      sourcekey: 'healthCalorieValue',
      interval: 100,
      values: newGetValues(
        0,
        182,
        Number(dataSourceDefault.healthCalorieValue),
        10
      )
    },
    {
      sourcekey: 'healthCalorieProgress',
      interval: 110,
      values: newGetValues(
        0,
        100,
        Number(dataSourceDefault.healthCalorieProgress),
        10
      )
    },
    {
      sourcekey: 'healthCalorieTarget',
      interval: 120,
      values: newGetValues(
        0,
        223,
        Number(dataSourceDefault.healthCalorieTarget),
        10
      )
    },
    {
      sourcekey: 'healthStandCount',
      interval: 150,
      values: newGetValues(0, 24, Number(dataSourceDefault.healthStandCount), 1)
    },
    {
      sourcekey: 'healthStandProgress',
      interval: 110,
      values: newGetValues(
        0,
        100,
        Number(dataSourceDefault.healthStandProgress),
        10
      )
    },
    {
      sourcekey: 'healthStandTarget',
      interval: 150,
      values: newGetValues(
        0,
        12,
        Number(dataSourceDefault.healthStandTarget),
        1
      )
    },
    {
      sourcekey: 'healthExerciseDuration',
      interval: 120,
      values: newGetValues(
        0,
        44,
        Number(dataSourceDefault.healthExerciseDuration),
        1
      )
    },
    {
      sourcekey: 'healthExerciseProgress',
      interval: 110,
      values: newGetValues(
        0,
        100,
        Number(dataSourceDefault.healthExerciseProgress),
        10
      )
    },
    {
      sourcekey: 'healthExerciseTarget',
      interval: 120,
      values: newGetValues(
        0,
        50,
        Number(dataSourceDefault.healthExerciseTarget),
        10
      )
    },
    // 心率
    {
      sourcekey: 'healthHeartRate',
      interval: 80,
      values: newGetValues(
        0,
        150,
        Number(dataSourceDefault.healthHeartRate),
        10
      )
    },
    {
      sourcekey: 'healthHeartRateZone',
      interval: (22 * 120) / 4,
      values: [3, 4, 5, 6],
      unAssociate: true
    },
    {
      sourcekey: 'healthHeartRateMin',
      interval: 120,
      values: newGetValues(
        0,
        150,
        Number(dataSourceDefault.healthHeartRateMin),
        10
      )
    },
    {
      sourcekey: 'healthHeartRateMax',
      interval: 120,
      values: newGetValues(
        0,
        150,
        Number(dataSourceDefault.healthHeartRateMax),
        10
      )
    },
    {
      sourcekey: 'healthOxygenSpO2',
      interval: 500,
      values: newGetValues(
        0,
        100,
        Number(dataSourceDefault.healthOxygenSpO2),
        10
      )
    },
    {
      sourcekey: 'healthPressureIndex',
      interval: 120,
      values: newGetValues(
        0,
        100,
        Number(dataSourceDefault.healthPressureIndex),
        10
      )
    },
    {
      sourcekey: 'healthSleepDuration',
      interval: 80,
      values: newGetValues(
        0,
        24.0,
        Number(dataSourceDefault.healthSleepDuration),
        10
      )
    },
    {
      sourcekey: 'healthSleepTarget',
      interval: 75,
      values: newGetValues(
        0,
        24,
        Number(dataSourceDefault.healthSleepTarget),
        1
      )
    },
    {
      sourcekey: 'healthSleepTargetProgress',
      interval: 150,
      values: newGetValues(
        0,
        100,
        Number(dataSourceDefault.healthSleepTargetProgress),
        10
      )
    },
    {
      sourcekey: 'healthSleepHRV',
      interval: 525,
      values: [2, 3, 0, 1, 2]
    },
    // 废弃数据源
    // {
    //   sourcekey: 'healthMiscRecoveryTime',
    //   interval: 120,
    //   values: getValues(1, 96, 10)
    // },
    // {
    //   sourcekey: 'healthMiscRunPowerIndex',
    //   interval: 120,
    //   values: getValues(63, 80)
    // },
    // 血压舒张压
    {
      sourcekey: 'healthBloodDiastolicPressureMmhg',
      interval: 15,
      values: newGetValues(
        0,
        200,
        Number(dataSourceDefault.healthBloodDiastolicPressureMmhg),
        10
      )
    },
    // 血压收缩压
    {
      sourcekey: 'healthBloodSystolicPressureMmhg',
      interval: 13,
      values: newGetValues(
        0,
        200,
        Number(dataSourceDefault.healthBloodSystolicPressureMmhg),
        10
      )
    },
    // 血糖
    {
      sourcekey: 'healthBloodSugarValue',
      interval: 120,
      values: newGetValues(
        0,
        25.0,
        Number(dataSourceDefault.healthBloodSugarValue),
        10
      )
    },
    // 紫外线UVA
    {
      sourcekey: 'healthUVA',
      interval: 150,
      values: newGetValues(
        1,
        5,
        Number(dataSourceDefault.healthUVA),
        1
      )
    },
    // 紫外线UVB
    {
      sourcekey: 'healthUVB',
      interval: 150,
      values: newGetValues(
        1,
        5,
        Number(dataSourceDefault.healthUVB),
        1
      )
    },
    {
      sourcekey: 'weatherCurrentSunRiseHour',
      interval: 32,
      values: newGetValues(
        0,
        23,
        Number(dataSourceDefault.weatherCurrentSunRiseHour),
        1
      ),
      unAssociate: true
    },
    // 日出时间分钟
    {
      sourcekey: 'weatherCurrentSunRiseMinute',
      interval: 82,
      values: newGetValues(
        0,
        59,
        Number(dataSourceDefault.weatherCurrentSunRiseMinute),
        10
      ),
      unAssociate: true
    },
    {
      sourcekey: 'weatherCurrentSunSetHour',
      interval: 35,
      values: newGetValues(
        0,
        23,
        Number(dataSourceDefault.weatherCurrentSunSetHour),
        1
      ),
      unAssociate: true
    },
    {
      sourcekey: 'weatherCurrentSunSetMinute',
      interval: 60,
      values: newGetValues(
        0,
        59,
        Number(dataSourceDefault.weatherCurrentSunSetMinute),
        10
      ),
      unAssociate: true
    },
    {
      sourcekey: 'weatherCurrentTemperature',
      interval: 150,
      values: newGetValues(
        -99,
        99,
        Number(dataSourceDefault.weatherCurrentTemperature),
        10
      )
    },
    {
      sourcekey: 'weatherCurrentTemperatureFahrenheit',
      interval: 100,
      values: newGetValues(
        -147,
        211,
        Number(dataSourceDefault.weatherCurrentTemperatureFahrenheit),
        10
      )
    },
    {
      sourcekey: 'weatherTodayTemperatureMax',
      interval: 120,
      values: newGetValues(
        -99,
        99,
        Number(dataSourceDefault.weatherTodayTemperatureMax),
        10
      )
    },
    {
      sourcekey: 'weatherTodayTemperatureMin',
      interval: 120,
      values: newGetValues(
        -99,
        99,
        Number(dataSourceDefault.weatherTodayTemperatureMin),
        10
      )
    },
    {
      sourcekey: 'weatherTodayTemperatureMaxFahrenheit',
      interval: 100,
      values: newGetValues(
        -147,
        211,
        Number(dataSourceDefault.weatherTodayTemperatureMaxFahrenheit),
        10
      )
    },
    {
      sourcekey: 'weatherTodayTemperatureMinFahrenheit',
      interval: 100,
      values: newGetValues(
        -147,
        211,
        Number(dataSourceDefault.weatherTodayTemperatureMinFahrenheit),
        10
      )
    },
    // 暂时废弃
    // {
    //   sourcekey: 'weatherTomorrowTemperatureMax',
    //   interval: 120,
    //   values: getValues(28, 99, 10)
    // },
    // {
    //   sourcekey: 'weatherTomorrowTemperatureMin',
    //   interval: 120,
    //   values: getValues(20, 99, 10)
    // },
    // 体感温度
    // {
    //   sourcekey: 'weatherCurrentTemperatureFeel',
    //   interval: 150,
    //   values: getValues(25, 99, 10)
    // },
    {
      sourcekey: 'weatherCurrentHumidity',
      interval: 100,
      values: newGetValues(
        0,
        100,
        Number(dataSourceDefault.weatherCurrentHumidity),
        10
      )
    },
    {
      sourcekey: 'weatherCurrentWeather',
      interval: 180,
      values: [1, 2, 4, 5, 6, 19, 7, 8, 9, 13, 15, 16, 18, 29, 20, 53]
    },
    {
      sourcekey: DataSourceKey.tianSeFull,
      interval: 60,
      values: getValues(0, 39),
      unAssociate: true
    },
    {
      sourcekey: DataSourceKey.tianseSimple,
      interval: 60,
      values: getValues(0, 24),
      unAssociate: true
    },
    {
      sourcekey: 'weatherCurrentWindDirection',
      interval: 300,
      values: newGetValues(
        0,
        9,
        Number(dataSourceDefault.weatherCurrentWindDirection),
        1
      )
    },
    {
      sourcekey: 'weatherCurrentWindAngle',
      interval: 150,
      values: newGetValues(
        0,
        361,
        Number(dataSourceDefault.weatherCurrentWindAngle),
        10
      )
    },
    // 暂时废弃
    // {
    //   sourcekey: 'weatherCurrentWindSpeed',
    //   interval: 150,
    //   values: getValues(10, 30)
    // },
    {
      sourcekey: 'weatherCurrentWindLevel',
      interval: 250,
      values: newGetValues(
        0,
        12,
        Number(dataSourceDefault.weatherCurrentWindLevel),
        1
      )
    },
    {
      sourcekey: 'weatherCurrentAirQualityIndex',
      interval: 120,
      values: newGetValues(
        0,
        60,
        Number(dataSourceDefault.weatherCurrentAirQualityIndex),
        10
      )
    },
    {
      sourcekey: 'weatherAirQualityLevel',
      interval: 150,
      values: [0,1,2,3,4,5,0]
    },
    // 暂时废弃
    // {
    //   sourcekey: 'weatherCurrentChanceOfRain',
    //   interval: 250,
    //   values: getValues(30, 100, 10)
    // },

    // 当地海平面气压
    {
      sourcekey: 'weatherCurrentPressure',
      interval: 100,
      values: newGetValues(
        700,
        1100,
        Number(dataSourceDefault.weatherCurrentPressure),
        100
      )
    },
    // 海拔高度
    {
      sourcekey: 'systemSensorFusionAltitude',
      interval: 250,
      values: newGetValues(
        -200,
        103,
        Number(dataSourceDefault.systemSensorFusionAltitude),
        10
      )
    },
    // 暂时废弃
    // 能见度
    // {
    //   sourcekey: 'weatherCurrentVisibility',
    //   interval: 250,
    //   values: getValues(15, 100, 10)
    // },
    // 紫外线指数
    {
      sourcekey: 'weatherCurrentUVIndex',
      interval: 150,
      values: newGetValues(
        0,
        15,
        Number(dataSourceDefault.weatherCurrentUVIndex),
        1
      )
    },
    // 暂时废弃
    // 穿衣指数
    // {
    //   sourcekey: 'weatherCurrentDressIndex',
    //   interval: 300,
    //   values: getValues(2, 7)
    // },
    {
      sourcekey: 'systemStatusBattery',
      interval: 110,
      values: newGetValues(
        0,
        100,
        Number(dataSourceDefault.systemStatusBattery),
        10
      )
    },
    {
      sourcekey: 'systemStatusCharge',
      interval: 300,
      values: [0, 1, 0]
    },
    {
      sourcekey: 'systemStatusDisturb',
      interval: 300,
      values: [0, 1, 0]
    },
    {
      sourcekey: 'systemStatusBluetooth',
      interval: 300,
      values: [0, 1, 0]
    },
    {
      sourcekey: 'systemStatusWifi',
      interval: 300,
      values: [0, 1, 0]
    },
    {
      sourcekey: 'systemStatusScreenLock',
      interval: 300,
      values: [0, 1, 0]
    },
    {
      sourcekey: 'appAlarmHour',
      interval: 120,
      values: newGetValues(0, 23, Number(dataSourceDefault.appAlarmHour), 1),
      unAssociate: true
    },
    {
      sourcekey: 'appAlarmMinute',
      interval: 66,
      values: newGetValues(0, 59, Number(dataSourceDefault.appAlarmMinute), 10),
      unAssociate: true
    },
    // 实时气压
    {
      sourcekey: 'systemSensorAtmosphericPressure',
      interval: 100,
      values: newGetValues(
        300,
        1100,
        Number(dataSourceDefault.systemSensorAtmosphericPressure),
        100
      )
    },
    // 指南针角度
    {
      sourcekey: 'systemSensorCompass',
      interval: 200,
      values: newGetValues(0, 359, 0, 10)
    },
    // 城市列表
    {
      sourcekey: 'dualtimeCity',
      interval: 200,
      values: newGetValues(0, 42, Number(dataSourceDefault.dualtimeCity), 1)
    },
    // 星期
    {
      sourcekey: 'dateWeek',
      interval: (930 * 3) / 2,
      values: newGetValues(0, 6, Number(dataSourceDefault.dateWeek), 1),
      unAssociate: true
    },
    // 时间段
    {
      sourcekey: 'miscTimeSection',
      interval: 525,
      values: newGetValues(0, 8, Number(dataSourceDefault.miscTimeSection), 1),
      unAssociate: true
    },
    // 禁飞时间
    {
      sourcekey: 'appSportNoFlyTime',
      interval: 10,
      values: newGetValues(0, 23, 18, 1),
      unAssociate: true
    },
    // 水面间隔时间
    {
      sourcekey: 'appSportSitTime',
      interval: 100,
      // 从35分钟-59分钟
      values: newGetValues(
        0,
        59,
        Number(dataSourceDefault.appSportSitTime),
        10
      ),
      unAssociate: true
    },

    // 天干地支
    {
      sourcekey: 'dateSexagenaryCycle',
      interval: 120,
      values: newGetValues(0, 59, ganzhiIndex, 10),
      unAssociate: true
    },
    // 睡眠质量
    {
      sourcekey: 'healthSleepQuality',
      interval: 150,
      values: [3, 4, 0, 1, 2, 3]
    },
    // 体能状态
    {
      sourcekey: 'appSportTrainingStatus',
      interval: 525,
      values: [2, 3, 4, 0, 1, 2]
    },
    // 近7日训练负荷
    {
      sourcekey: 'appSportTrainingLoad',
      interval: 525,
      values: [2, 3, 0, 1, 2]
    },
    // { 
    //   sourcekey: 'appSportWeeklyLoadScore',
    //   interval: 60,
    //   values: newGetValues(0,2500,100,100)
    // },
    // 恢复时间
    {
      sourcekey: 'appSportRecoveryTime',
      interval: 200,
      values: newGetValues(
        0,
        96,
        Number(dataSourceDefault.appSportRecoveryTime),
        10
      )
    },
    {
      sourcekey: 'appSportRecoveryPercentage',
      interval: 90,
      values: newGetValues(0,100,90,10)
    },
    // 跑力指数
    {
      sourcekey: 'appSportRunPowerIndex',
      interval: 525,
      values: [3, 4, 5, 0, 1, 2, 3]
    },
    // 最大摄氧量
    {
      sourcekey: 'appSportOxygenUpdateMax',
      interval: 525,
      values: [5, 6, 7, 0, 1, 2, 3, 4, 5]
    },
    // 今日最高压力
    {
      sourcekey: 'healthPressureMax',
      interval: 200,
      values: newGetValues(
        0,
        100,
        Number(dataSourceDefault.healthPressureMax),
        10
      )
    },
    // 今日最低压力
    {
      sourcekey: 'healthPressureMin',
      interval: 200,
      values: newGetValues(
        0,
        100,
        Number(dataSourceDefault.healthPressureMin),
        10
      )
    },
    // 今日最高血氧
    {
      sourcekey: 'healthOxygenSpO2Max',
      interval: 200,
      values: newGetValues(
        0,
        100,
        Number(dataSourceDefault.healthOxygenSpO2Max),
        10
      )
    },
    // 今日最低血氧
    {
      sourcekey: 'healthOxygenSpO2Min',
      interval: 200,
      values: newGetValues(
        0,
        100,
        Number(dataSourceDefault.healthOxygenSpO2Min),
        10
      )
    },
    // 女性距离下一经期时间
    {
      sourcekey: 'healthMenstrualCountdown',
      interval: 200,
      values: newGetValues(
        0,
        90,
        Number(dataSourceDefault.healthMenstrualCountdown),
        10
      )
    }
  ];

  // 获取需要的keys
  const unAssociateKeys = wholeItems
    .filter((item) => item.unAssociate)
    .map((item) => item.sourcekey);

  const keepKeys: string[] = [];
  recordSourceKeys.forEach((key) => {
    // 单独处理一下日期
    if (
      key.startsWith('dateWeekString') ||
      ['miscdateTomorrowWeak', 'miscdateYestardayWeak'].includes(key)
    ) {
      key = 'dateWeek';
    }
    // 处理天干地支字符串
    if (key == 'dateLunarStringYear') {
      key = 'dateSexagenaryCycle';
    }
    // 判断是否需要关联
    if (unAssociateKeys.includes(key)) {
      keepKeys.push(key);
    } else {
      keepKeys.push(...toRelatedSourceKeys(key));
    }
  });

  const _items = cloneDeep(
    wholeItems.filter((item) => keepKeys.includes(item.sourcekey))
  );

  // 统一时长，动效总时长规定为2s
  if (_items.length) {
    const total = 3200;
    _items.forEach((item) => {
      const count = item.values.length;
      item.interval = Math.ceil(total / count);
    });
  }

  // 为解决到最后一帧后，会迅速停止，导致最后一帧无法显示的问题，在末尾添加一个最后一帧数值
  _items.forEach((item) => {
    item.values.push(item.values[item.values.length - 1]);
  });

  return _items;
};
